<template>
  <v-card class="board-card mb-2 pa-2" outlined :class="{ 'cb-technical': booking.technical }">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center" style="cursor: pointer" @click="openBooking">
        <v-icon v-if="booking.in_rent" small color="primary" class="mr-1">mdi-car</v-icon>
        <v-icon v-else-if="booking.active" small color="secondary" class="mr-1">mdi-clock-outline</v-icon>
        <v-icon v-if="booking.failure" small class="mr-1">mdi-close-circle-outline</v-icon>
        <span class="font-weight-medium">№{{ booking.id }}</span>
        <v-tooltip bottom v-if="booking.tasks_count > 0">
          <template v-slot:activator="{ on, attrs }">
            <v-chip x-small class="ml-1 px-2" :color="tasksOverdue ? 'error' : 'primary'" text-color="white"
              style="cursor: pointer" v-on="on" v-bind="attrs" @click.stop="$emit('open-tasks', booking)">
              {{ booking.tasks_count }}
            </v-chip>
          </template>
          <div>
            <div v-for="(t, i) in booking.tasks_preview" :key="i">
              • {{ t.title }}<template v-if="t.due_at"> — {{ formatDue(t.due_at) }}</template>
              <span v-if="t.overdue"> ({{ $t("board.overdue") }})</span>
            </div>
          </div>
        </v-tooltip>
      </div>
      <div class="d-flex align-center">
        <!-- Status chip IS the status-change menu activator (like the bookings list). -->
        <v-menu v-if="canAct" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-chip x-small :color="stateColor" text-color="white" style="cursor: pointer"
              v-on="on" v-bind="attrs" @click.stop>
              <v-icon x-small left>{{ stateIcon }}</v-icon>
              {{ stateLabel }}
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item v-for="(st, i) in states" :key="i" @click="changeStatus(st.value)">
              <v-list-item-icon><v-icon :color="getColor(st.value)">{{ getIcon(st.value) }}</v-icon></v-list-item-icon>
              <v-list-item-title>{{ st.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-chip v-else x-small :color="stateColor" text-color="white">
          <v-icon x-small left>{{ stateIcon }}</v-icon>
          {{ stateLabel }}
        </v-chip>

        <!-- Overflow kebab: the OTHER actions (not status). -->
        <!-- Не canAct: в этом меню нет ни статуса, ни описания — только
             действия сотрудника. Создание задачи ушло бы в /tasks и получило
             403 (ensure_staff!) — тот самый инцидент 12.08; рассылки идут
             клиенту от имени компании; архивация закрыта агенту и в карточке
             брони. Агент с can_edit_booking меняет статус и описание (chip
             и поле выше), а бронь клиенту подтверждает из её карточки. -->
        <v-menu v-if="isStaff" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon x-small class="ml-1" :loading="acting" v-on="on" v-bind="attrs" @click.stop>
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="$emit('open-tasks', booking)">
              <v-list-item-icon><v-icon>mdi-clipboard-plus-outline</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("board.add_task") }}</v-list-item-title>
            </v-list-item>
            <template v-if="canNotify && isBacklog">
              <v-list-item @click="openChannels('confirm')">
                <v-list-item-icon><v-icon color="success">mdi-check-all</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t("bookings.send_approve") }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openChannels('decline')">
                <v-list-item-icon><v-icon color="warning">mdi-cancel</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t("bookings.decline_simple") }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('send-template', booking)">
                <v-list-item-icon><v-icon color="primary">mdi-email-outline</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t("bookings.from_template") }}</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item v-if="!booking.active" @click="archive">
              <v-list-item-icon><v-icon>mdi-archive</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("board.archive") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <router-link
      class="text-body-2 mt-1 d-block text-decoration-none"
      :to="{ path: `/cars/${booking.car_id}` }"
      @click.native.stop
    >
      {{ booking.car_name }}({{ booking.car_code }})
    </router-link>
    <router-link
      class="text-caption text-decoration-none"
      :to="{ name: 'ClientCard', params: { id: booking.client_id } }"
      @click.native.stop
    >
      {{ booking.full_name }}
    </router-link>
    <div class="text-body-2 font-weight-medium mt-1" :style="lateStyle">
      {{ formatDate(booking.start_date) }} {{ formatTime(booking.start_date) }}
      → {{ formatDate(booking.end_date) }} {{ formatTime(booking.end_date) }}
      <span class="text--secondary">· {{ booking.days }}</span>
    </div>
    <div class="text-caption" v-if="booking.location_start || booking.location_end">
      {{ booking.location_start }} → {{ booking.location_end }}
    </div>
    <div class="d-flex align-center justify-space-between mt-1">
      <!-- Чип оплаты (paymentStatus.js): расчётная подпись с валютой при наличии
           платежей или идущей/завершённой аренде, контурный «не оплачено» без
           платежей, у технических броней без платежей чипа нет. -->
      <v-chip v-if="paymentChip" data-cy="board-payment-chip"
        x-small :color="paymentChip.color" :outlined="paymentChip.outlined"
        :text-color="paymentChip.outlined ? undefined : 'white'">
        {{ paymentChip.label }}
      </v-chip>
      <span v-else></span>
      <template v-if="canAct">
        <v-text-field
          v-if="editingDesc"
          v-model="descDraft"
          dense
          hide-details
          autofocus
          class="text-caption pa-0 ma-0"
          style="max-width: 60%"
          :loading="savingDesc"
          @click.stop
          @keyup.enter="saveDesc"
          @keyup.esc="cancelDesc"
          @blur="saveDesc"
        />
        <v-tooltip v-else-if="booking.description" bottom>
          <template v-slot:activator="{ on, attrs }">
            <span class="text-caption text-truncate" style="max-width: 60%; cursor: text"
              v-on="on" v-bind="attrs" @click.stop="startEditDesc">
              {{ booking.description }}
            </span>
          </template>
          <span>{{ booking.description }}</span>
        </v-tooltip>
        <v-icon v-else x-small style="cursor: text" @click.stop="startEditDesc">mdi-pencil-outline</v-icon>
      </template>
      <v-tooltip v-else-if="booking.description" bottom>
        <template v-slot:activator="{ on, attrs }">
          <span class="text-caption text-truncate" style="max-width: 60%" v-on="on" v-bind="attrs">
            {{ booking.description }}
          </span>
        </template>
        <span>{{ booking.description }}</span>
      </v-tooltip>
    </div>

    <!-- FT-082: big "archive" button revealed only on hover over a finished booking -->
    <div v-if="booking.ride" class="cb-archive">
      <v-btn block small color="warning" outlined @click.stop="archive">
        <v-icon left small>mdi-archive-arrow-down</v-icon>{{ $t("board.archive") }}
      </v-btn>
    </div>

    <v-dialog v-model="channelDialog" max-width="320">
      <v-card class="pa-3">
        <div class="text-subtitle-2 mb-2">
          {{ channelMode === "confirm" ? $t("bookings.send_approve") : $t("bookings.decline_simple") }}
        </div>
        <v-checkbox v-model="channels.email" label="Email" hide-details dense />
        <v-checkbox v-model="channels.whatsapp" label="WhatsApp" hide-details dense />
        <v-checkbox v-model="channels.sms" label="SMS" hide-details dense />
        <div class="d-flex justify-end mt-2">
          <v-btn text small @click="channelDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" small :loading="acting"
            :disabled="!channels.email && !channels.whatsapp && !channels.sms" @click="sendChannels">
            {{ $t("custom.send") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import bookingActionsMixin from "@/components/dashboard/bookings/board/bookingActionsMixin";
import { translateBookingState } from "@/utils/localeInfra";

export default {
  name: "BookingBoardCard",
  mixins: [bookingActionsMixin],
  computed: {
    // Статусы в БД лежат по-русски; на карточке показываем перевод локали.
    stateLabel() {
      return translateBookingState((k) => this.$t(k), this.booking.state);
    },
  },
};
</script>

<style scoped>
/* FT-082: technical bookings get a quiet blue-grey wash (like the list), works on
   both light and dark themes via translucency. */
.cb-technical {
  background-color: rgba(96, 125, 139, 0.14) !important;
}
/* archive button on rided cards: smoothly expands/fades in on card hover
   (animate max-height + opacity + margin so neither the button nor the card jumps) */
.cb-archive {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  overflow: hidden;
  transition: max-height 0.22s ease, opacity 0.22s ease, margin-top 0.22s ease;
}
.board-card:hover .cb-archive {
  max-height: 44px;
  opacity: 1;
  margin-top: 8px;
}
</style>
